/**
 * Wire encoding for the `model3dLight` action.
 *
 * Encoder and decoder share one pattern on purpose: the firmware has no final
 * `3d.light` command yet, so this module is the single place to change once it does.
 * The four pieces of information that must survive are target object, light id,
 * property and value.
 */
import { parseRuntimePropertyValue, serializeRuntimePropertyValue } from './propertyValueCodec.js';

/**
 * Properties a 3D light exposes to actions. Adding `color` or `enabled` later is a
 * data change here — the action shape, editor and wire format stay as they are.
 *
 * Intensity is a 0..100 percentage of the intensity the light carries in the GLB, which
 * is what the preview applies (see `h3dLightRuntime.js`) and what lets a plain 0..100
 * slider drive a light whose absolute glTF intensity is in the tens of thousands.
 */
export const H3D_LIGHT_PROPERTIES = [
    {
        id: 'intensity',
        label: 'Intensity',
        type: 'number',
        metadata: { type: 'number', min: 0, max: 100, step: 1 },
        default: 100,
    },
];

const LIGHT_ID = '[A-Za-z0-9_-]+';
const LIGHT_ID_PATTERN = new RegExp(`^${LIGHT_ID}$`);
const COMMAND_PATTERN = new RegExp(`^p(\\d+)b(\\d+)\\.light\\.(${LIGHT_ID})\\.(${LIGHT_ID})=([\\s\\S]*)$`);

export function getH3dLightProperty(id) {
    return H3D_LIGHT_PROPERTIES.find(property => property.id === id) ?? null;
}

export function isValidH3dLightId(light) {
    return LIGHT_ID_PATTERN.test(String(light ?? ''));
}

export function serializeH3dLightAction(action) {
    const pageId = Number(action?.target?.pageId);
    const objectId = Number(action?.target?.objectId);
    if (!Number.isInteger(pageId) || !Number.isInteger(objectId)) return null;

    const light = String(action?.light ?? '');
    if (!isValidH3dLightId(light)) return null;

    const property = getH3dLightProperty(action?.property);
    if (!property) return null;

    const value = serializeRuntimePropertyValue(action.value, property.metadata);
    if (value === null) return null;

    return `p${pageId}b${objectId}.light.${light}.${property.id}=${value}`;
}

export function parseH3dLightCommand(command) {
    const match = COMMAND_PATTERN.exec(String(command ?? ''));
    if (!match) return null;

    const property = getH3dLightProperty(match[4]);
    if (!property) return null;

    const value = parseRuntimePropertyValue(match[5], property.metadata);
    if (value === null) return null;

    return {
        type: 'model3dLight',
        target: { pageId: Number(match[1]), objectId: Number(match[2]) },
        light: match[3],
        property: property.id,
        value,
    };
}
